import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type ParseCvResult,
  checkUsernameAvailability,
  saveProfileSetup,
  setIntent as setIntentApi,
  saveOnboardingCompany,
  saveOnboardingPreferences,
  getOnboardingCompany,
  createAssetUploadUrl,
  parseCvFile,
  parseCvText,
  saveSections,
  completeOnboarding as completeOnboardingApi,
  getOnboardingState,
  getOnboardingProfile,
  suggestSkills,
  suggestJobTitles,
  generateHighlights,
  enhanceText,
  generateSummary,
  generateTitle,
  draftExperience,
  draftEducation,
  getResumeSource,
  revertToSource,
  type RevertToSourceResult,
  type ResumeSourceData,
} from "../lib/onboarding-api";
import type {
  OnboardingIntent,
  UsernameCheckResult,
  OnboardingCompanyProfile,
  CreateImageUploadUrlResult,
  SaveOnboardingCompanyInput,
  SaveOnboardingCompanyResult,
  AISuggestSkillsResult,
  AIGenerateHighlightsResult,
  AIEnhanceTextResult,
  AIDraftExperienceInput,
  AIDraftExperienceResult,
  AIDraftEducationInput,
  AIDraftEducationResult,
  OnboardingPreferencesInput,
  OnboardingPreferencesResult,
} from "@tabbio/types";
import {
  mergeAuthenticatedUser,
  refreshAuthenticatedUser,
} from "../lib/app-auth";
import { QUERY_KEYS } from "../lib/query-keys";

// ─── Username Check (debounced) ────────────────────────────────

export function useCheckUsername(username: string) {
  const [debouncedUsername, setDebouncedUsername] = useState("");

  useEffect(() => {
    const normalized = username.toLowerCase().trim();
    if (normalized.length < 3) {
      setDebouncedUsername("");
      return;
    }

    const timer = setTimeout(() => setDebouncedUsername(normalized), 400);
    return () => clearTimeout(timer);
  }, [username]);

  const query = useQuery<UsernameCheckResult>({
    queryKey: ["onboarding", "username", debouncedUsername],
    queryFn: () => checkUsernameAvailability(debouncedUsername),
    enabled: debouncedUsername.length >= 3,
    staleTime: 10_000,
    retry: false,
  });

  return {
    isAvailable: query.data?.available ?? null,
    reason: query.data?.reason,
    suggestion: query.data?.suggestion,
    isChecking: query.isFetching,
    // The value the current result actually corresponds to. Consumers compare
    // this against their live input to ignore results left over from the
    // debounce window (a stale "available" must never gate a Continue button).
    checkedUsername: debouncedUsername,
    error: query.error,
  };
}

// ─── Save Profile Setup ────────────────────────────────────────

export function useSaveProfileSetup() {
  return useMutation({
    mutationFn: (data: {
      name: string;
      username?: string;
      nationality?: string;
      phone?: string;
      avatarUrl?: string | null;
    }) => saveProfileSetup(data),
  });
}

// ─── Set Intent ────────────────────────────────────────────────

export function useSetIntent() {
  return useMutation({
    mutationFn: (intent: OnboardingIntent) => setIntentApi(intent),
  });
}

export function useSaveOnboardingPreferences() {
  return useMutation<
    OnboardingPreferencesResult,
    Error,
    OnboardingPreferencesInput
  >({
    mutationFn: (data) => saveOnboardingPreferences(data),
  });
}

// ─── Company Setup ─────────────────────────────────────────────

export function useSaveOnboardingCompany() {
  return useMutation<
    SaveOnboardingCompanyResult,
    Error,
    SaveOnboardingCompanyInput
  >({
    mutationFn: (data) => saveOnboardingCompany(data),
  });
}

export function useOnboardingCompany(enabled: boolean) {
  return useQuery<OnboardingCompanyProfile | null>({
    queryKey: ["onboarding", "company"],
    queryFn: getOnboardingCompany,
    enabled,
    staleTime: Infinity,
  });
}

export function useCreateAssetUploadUrl() {
  return useMutation<
    CreateImageUploadUrlResult,
    Error,
    Parameters<typeof createAssetUploadUrl>[0]
  >({
    mutationFn: (params) => createAssetUploadUrl(params),
  });
}

// ─── Parse CV ──────────────────────────────────────────────────

export function useParseCv() {
  return useMutation<
    ParseCvResult,
    Error,
    {
      file?: { uri: string; name: string; type: string };
      text?: string;
      skipEnrichment?: boolean;
    }
  >({
    mutationFn: async ({ file, text, skipEnrichment }) => {
      if (text) {
        return parseCvText(text, { skipEnrichment });
      }

      if (file) {
        const formData = new FormData();
        formData.append("file", {
          uri: file.uri,
          name: file.name,
          type: file.type,
        } as any);
        return parseCvFile(formData, { skipEnrichment });
      }

      throw new Error("Either file or text must be provided");
    },
  });
}

// ─── Save Sections ─────────────────────────────────────────────

export function useSaveSections() {
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => saveSections(data),
  });
}

// ─── Complete Onboarding ───────────────────────────────────────

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => completeOnboardingApi(),
    onSuccess: async () => {
      mergeAuthenticatedUser({ isOnboardingCompleted: true });
      const refreshedUser = await refreshAuthenticatedUser().catch(() => null);
      if (!refreshedUser?.isOnboardingCompleted) {
        mergeAuthenticatedUser({ isOnboardingCompleted: true });
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["onboarding"] }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.WORKSPACES(refreshedUser?.id),
        }),
      ]);
    },
  });
}

// ─── Get Onboarding State ──────────────────────────────────────

export function useOnboardingState() {
  return useQuery({
    queryKey: ["onboarding", "state"],
    queryFn: getOnboardingState,
    staleTime: Infinity,
  });
}

// ─── Get Full Profile (for resume on refresh) ──────────────────

export function useOnboardingProfile(enabled: boolean) {
  return useQuery({
    queryKey: ["onboarding", "profile"],
    queryFn: getOnboardingProfile,
    enabled,
    staleTime: Infinity,
  });
}

// ─── AI: Suggest Skills ────────────────────────────────────────

export function useAISuggestSkills() {
  return useMutation<
    AISuggestSkillsResult,
    Error,
    {
      title?: string;
      experience?: {
        title: string;
        company: string;
        description?: string;
        achievements?: string[];
      }[];
      education?: { school: string; degree?: string; fieldOfStudy?: string }[];
      existingSkills?: string[];
    }
  >({
    mutationFn: (params) => suggestSkills(params),
  });
}

// ─── AI: Suggest Job Titles ────────────────────────────────────

export function useAISuggestJobTitles() {
  return useMutation<
    { titles: string[] },
    Error,
    {
      currentTitle?: string;
      experience?: {
        title: string;
        company: string;
        description?: string;
        achievements?: string[];
        current?: boolean;
      }[];
      topSkills?: string[];
      existingTitles?: string[];
    }
  >({
    mutationFn: (params) => suggestJobTitles(params),
  });
}

// ─── AI: Generate Highlights ───────────────────────────────────

export function useAIGenerateHighlights() {
  return useMutation<
    AIGenerateHighlightsResult,
    Error,
    {
      name?: string;
      title?: string;
      experience?: { title: string; company: string; achievements: string[] }[];
      certifications?: { name: string; issuer: string }[];
      skills?: string[];
      existingHighlights?: { title: string; description: string }[];
    }
  >({
    mutationFn: (params) => generateHighlights(params),
  });
}

// ─── AI: Generate Summary ──────────────────────────────────────

export function useAIGenerateSummary() {
  return useMutation<
    { summary: string },
    Error,
    {
      name?: string;
      title?: string;
      experience?: {
        title: string;
        company: string;
        startDate?: string;
        endDate?: string;
        current?: boolean;
        employmentType?: string;
        location?: string;
        locationType?: string;
      }[];
      education?: { school: string; degree?: string; fieldOfStudy?: string }[];
      certifications?: { name: string; issuer: string }[];
      skills?: string[];
      careerHighlights?: { title: string; description: string }[];
    }
  >({
    mutationFn: (params) => generateSummary(params),
  });
}

export function useAIDraftExperience() {
  return useMutation<AIDraftExperienceResult, Error, AIDraftExperienceInput>({
    mutationFn: (params) => draftExperience(params),
  });
}

export function useAIDraftEducation() {
  return useMutation<AIDraftEducationResult, Error, AIDraftEducationInput>({
    mutationFn: (params) => draftEducation(params),
  });
}

// ─── AI: Generate Title ────────────────────────────────────────

export function useAIGenerateTitle() {
  return useMutation<
    { titleOptions: string[] },
    Error,
    {
      latestJobTitle?: string;
      totalYears?: number;
      topSkills?: string[];
      industry?: string;
      summary?: string;
      isCareerChanger?: boolean;
      targetRole?: string;
      name?: string;
      experience?: {
        title?: string;
        company?: string;
        startDate?: string;
        endDate?: string;
        current?: boolean;
        employmentType?: string;
        location?: string;
        locationType?: string;
        achievements?: string[];
      }[];
      education?: {
        school?: string;
        degree?: string;
        fieldOfStudy?: string;
      }[];
      certifications?: { name?: string; issuer?: string }[];
      careerHighlights?: { title?: string; description?: string }[];
      languages?: string[];
    }
  >({
    mutationFn: (params) => generateTitle(params),
  });
}

// ─── AI: Enhance Text ──────────────────────────────────────────

export function useAIEnhanceText() {
  return useMutation<
    AIEnhanceTextResult,
    Error,
    {
      text: string;
      field: string;
      scope?: "section" | "full_cv" | "apply_skill";
      context?: { title?: string; company?: string; skills?: string[] };
    }
  >({
    mutationFn: (params) => enhanceText(params),
  });
}

// ─── Resume Source ─────────────────────────────────────────────

export function useResumeSource() {
  return useQuery<ResumeSourceData | null>({
    queryKey: ["onboarding", "resume-source"],
    queryFn: getResumeSource,
    // Keep `canRevert` honest. A long staleTime let the Revert button linger
    // as a "ghost" after the backup was consumed elsewhere (a prior revert, a
    // second surface), so tapping it hit "No previous CV backup found".
    staleTime: 30_000,
    refetchOnMount: true,
  });
}

export function useRevertToSource() {
  const queryClient = useQueryClient();
  return useMutation<RevertToSourceResult, Error, void>({
    mutationFn: () => revertToSource(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["onboarding", "profile"],
      });
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
      void queryClient.invalidateQueries({ queryKey: ["cv"] });
      void queryClient.invalidateQueries({ queryKey: ["cvs"] });
    },
    // Refetch resume-source whether the revert succeeded OR failed, so
    // `canRevert` re-syncs and a stale Revert button can never persist.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["onboarding", "resume-source"],
      });
    },
  });
}
